import { useState } from 'react'
import { Button, Grid, HStack, Text } from '@chakra-ui/react'
import { UserSection } from './UserSection'
import { setAccountDisabled } from '../../services/userAdmin'
import { requestPasswordReset } from '../../services/firebaseService'
import { formatAuthDate } from '../../utils/userDisplay'
import { useTranslation } from '../../i18n'

function Field({ label, children, mono }) {
    return (
        <>
            <Text fontSize="sm" color="var(--mc-text-muted)">{label}</Text>
            <Text
                fontSize="sm"
                color="var(--mc-text)"
                wordBreak="break-all"
                fontFamily={mono ? "'JetBrains Mono', monospace" : undefined}
            >
                {children}
            </Text>
        </>
    )
}

export function AccountCard({ user, isSelf, act }) {
    const { t, lang } = useTranslation()
    const [busy, setBusy] = useState(null)

    const run = async (key, fn, success) => {
        setBusy(key)
        await act(fn, success)
        setBusy(null)
    }

    const toggleDisabled = () => run('disabled',
        () => setAccountDisabled({ uid: user.uid, disabled: !user.disabled }),
        user.disabled ? t('users.account.enabledToast') : t('users.account.disabledToast'))

    // Same continue URL as the login page's own "forgot password".
    const sendReset = () => run('reset',
        () => requestPasswordReset({ email: user.email, lang, continueUrl: `${window.location.origin}/` }),
        t('users.account.resetSent', { email: user.email }))

    return (
        <UserSection id="user-account" title={t('users.account.title')}>
            <Grid templateColumns={{ base: '1fr', sm: '160px 1fr' }} columnGap={4} rowGap={2}>
                <Field label={t('users.account.email')}>{user.email || '—'}</Field>
                <Field label={t('users.account.name')}>{user.displayName || user.profile?.name || '—'}</Field>
                <Field label={t('users.account.uid')} mono>{user.uid}</Field>
                <Field label={t('users.account.providers')}>
                    {user.providers.length ? user.providers.join(', ') : t('users.anonymous')}
                </Field>
                <Field label={t('users.account.created')}>{formatAuthDate(user.createdAt, lang) || '—'}</Field>
                <Field label={t('users.account.lastSignIn')}>
                    {formatAuthDate(user.lastSignInAt, lang) || t('users.neverSignedIn')}
                </Field>
                <Field label={t('users.account.status')}>
                    {user.disabled ? t('users.account.disabled') : t('users.account.enabled')}
                </Field>
            </Grid>
            {isSelf ? (
                <Text fontSize="sm" color="var(--mc-text-muted)">{t('users.account.selfNote')}</Text>
            ) : (
                <HStack gap={2} wrap="wrap">
                    <Button size="sm" variant="outline" onClick={toggleDisabled} loading={busy === 'disabled'}>
                        {user.disabled ? t('users.account.enable') : t('users.account.disable')}
                    </Button>
                    {user.email && (
                        <Button size="sm" variant="outline" onClick={sendReset} loading={busy === 'reset'}>
                            {t('users.account.sendReset')}
                        </Button>
                    )}
                </HStack>
            )}
        </UserSection>
    )
}
